import { Button, Progress, Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import type { TenantOverview } from '../../types'
import { formatBytes } from '../../ui/bytesFormat'

function pct(used: number | string, limit: number | string | null | undefined): number | null {
  if (limit == null || limit === '') return null
  const u = Number(used)
  const l = Number(limit)
  if (!Number.isFinite(u) || !Number.isFinite(l) || l <= 0) return null
  return Math.max(0, Math.min(100, Math.round((u / l) * 1000) / 10))
}

export default function PlanUsageTab({
  overview,
  onManagePlan,
  isTeamMember,
}: {
  overview: TenantOverview
  onManagePlan: () => void
  isTeamMember: boolean
}) {
  const { t } = useTranslation(['overview'])
  const plan = overview.plan
  const usage = overview.period_usage
  const storage = overview.storage
  const planName = plan.plan_display_name || plan.label

  const rows = [
    {
      key: 'images',
      label: t('overview:tabs.v3.plan.images'),
      used: usage?.images.used ?? 0,
      limit: usage?.images.limit,
    },
    {
      key: 'videos',
      label: t('overview:tabs.v3.plan.videos'),
      used: usage?.videos.used ?? 0,
      limit: usage?.videos.limit,
    },
    {
      key: 'text',
      label: t('overview:tabs.v3.plan.aiChat'),
      used: usage?.text.used ?? 0,
      limit: usage?.text.limit,
    },
    {
      key: 'audio',
      label: t('overview:tabs.v3.plan.audio'),
      used: usage?.audio.used ?? 0,
      limit: usage?.audio.limit,
    },
    {
      key: 'storage',
      label: t('overview:tabs.v3.plan.storage'),
      used: storage.used_bytes,
      limit: storage.quota_bytes,
      format: (v: number | string) => formatBytes(Number(v) || 0),
    },
  ]

  return (
    <div className="tc-ov-v3-plan" data-testid="overview-plan-tab">
      <div className="tc-ov-v3-top-row">
        <div className="tc-data-card tc-ov-v3-card">
          <div className="tc-ov-v2-plan-label">{t('overview:tabs.v3.plan.current')}</div>
          <div className="tc-ov-v2-plan-name">{planName}</div>
          <div className="tc-ov-v2-plan-meta">
            <Tag color={plan.status === 'active' ? 'green' : 'default'}>{plan.status}</Tag>
            {plan.expires_at ? null : <span>{t('overview:cards.plan.noExpiry')}</span>}
          </div>
          <Button type="primary" onClick={onManagePlan}>
            {isTeamMember ? t('overview:tabs.v3.plan.viewTeamPlan') : t('overview:tabs.v3.plan.upgrade')}
          </Button>
        </div>
        <div className="tc-data-card tc-ov-v3-card">
          <div className="tc-ov-panel-title">{t('overview:tabs.v3.plan.benefits')}</div>
          <ul className="tc-ov-v3-benefits">
            {plan.plan_credit_grant != null ? (
              <li>{t('overview:tabs.v3.plan.benefitCredits', { n: plan.plan_credit_grant })}</li>
            ) : null}
            {plan.plan_base_storage_bytes != null ? (
              <li>{t('overview:tabs.v3.plan.benefitStorage', { size: formatBytes(plan.plan_base_storage_bytes) })}</li>
            ) : null}
            <li>{t('overview:tabs.v3.plan.benefitFeatures')}</li>
          </ul>
        </div>
      </div>

      <div className="tc-data-card tc-ov-v3-card" data-testid="overview-plan-usage">
        <div className="tc-ov-panel-title">{t('overview:tabs.v3.plan.usageTitle')}</div>
        <div className="tc-ov-v3-usage-bars">
          {rows.map((row) => {
            const p = pct(row.used, row.limit ?? null)
            const usedLabel = row.format ? row.format(row.used) : String(row.used)
            const limitLabel =
              row.limit == null || row.limit === ''
                ? t('overview:tabs.v3.plan.unlimited')
                : row.format
                  ? row.format(row.limit)
                  : String(row.limit)
            return (
              <div key={row.key} className="tc-ov-v3-usage-bar-row">
                <div className="tc-ov-v3-usage-bar-head">
                  <span>{row.label}</span>
                  <span>
                    {usedLabel} / {limitLabel}
                    {p != null ? ` · ${p}%` : ''}
                  </span>
                </div>
                {p != null ? <Progress percent={p} showInfo={false} size="small" /> : null}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
